<template>
	<div class="columns-mapper" v-if="columnsMapping">
		<div v-for="userSelectedHeader in headers" :key="userSelectedHeader" class="column-editor flex-centerize mb-2">
			<IconButton class="eye-svg mr-3" @click="openColumnsPreviewDialog(userSelectedHeader)"><EyeSvg /></IconButton>
			<ColumnMappingEditor
				:header="userSelectedHeader"
				:columnOptions="getFieldPossibleSelections(userSelectedHeader)"
				:selection="getNewHeaderFromUserSelectedHeader(userSelectedHeader).value"
				:mergedColumnName="userSelectedMergingHeader"
				:isMergeMode="isExists(userSelectedMergingHeader)"
				@onMappingUpdated="onMappingUpdated"
				@setMappingEditorHoveredColumn="setMappingEditorHoveredColumn"
				@setMergeHeaders="setMergeHeaders"
				@onMergeColumn="mergeColumn"
				@cancelMerge="setMergeHeaders(null)"
				@splitMergedHeader="splitMergedHeader"
			/>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';

import EyeSvg from './assets/EyeSvg';
import ColumnMappingEditor from './ColumnMappingEditor';
import ColumnMapperService from './ColumnMapperService';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';

export default {
	name: 'ColumnsMapper',
	components: {
		ColumnMappingEditor,
		EyeSvg,
	},
	props: {
		columnsMapping: { type: Object },
		newHeaderRow: { type: Object },
		columnOptions: { type: Array },
		rows: { type: Array },
	},
	computed: {
		headers() {
			return Object.values(this.newHeaderRowMapping).sort((a, b) => (a < b ? -1 : 1));
		},
		selections() {
			return ColumnMapperService.getSelectedOptions(this.columnsMapping);
		},
		remainingColumnOptions() {
			const filteredColumnOptions = this.columnOptions.filter((co) => !this.selections.includes(co.value));
			const result = [];
			const divider = { divider: true };
			for (let i = 0; i < filteredColumnOptions.length; i++) {
				result.push(filteredColumnOptions[i], divider);
			}
			// add divider after every column option
			return result;
		},
		newHeaderRowMapping() {
			return ColumnMapperService.reduceFromColumnMapping({ ...this.newHeaderRow }, this.mergeMap);
		},
		columnsSample() {
			return ColumnMapperService.reduceFromColumnMapping({ ...this.newHeaderRow }, this.mergeMap);
		},
	},
	watch: {
		newHeaderRow: {
			handler() {
				this.mergeMap = {};
			},
		},
	},
	methods: {
		isExists(value) {
			return !isNullOrUndefinedOrEmpty(value);
		},
		getOriginalHeaderFromUserSelectedHeader(userSelectedHeader) {
			const headerMap = this.newHeaderRow;
			return Object.keys(headerMap).find((hr) => headerMap[hr] === userSelectedHeader);
		},
		onMappingUpdated(userSelectedHeader, newHeaderSelection) {
			const userSelectedHeaders = userSelectedHeader.split(' + ');
			const originHeaderToSet = userSelectedHeaders.map(this.getOriginalHeaderFromUserSelectedHeader);
			const newColumnsMap = ColumnMapperService.getNewMappedColumn(this.columnsMapping, newHeaderSelection, originHeaderToSet);
			this.$emit('updateState', { ...newColumnsMap });
		},
		setMergeHeaders(originHeader) {
			this.userSelectedMergingHeader = originHeader;
		},
		async mergeColumn(userSelectedHeader) {
			const header1 = this.getOriginalHeaderFromUserSelectedHeader(this.userSelectedMergingHeader);
			const header2 = this.getOriginalHeaderFromUserSelectedHeader(userSelectedHeader);
			const columnsSample = this.rows;
			const result = await AppDialogManagerService.openMergeColumnDialog({
				columnToMerge: { header: this.userSelectedMergingHeader, headerOrigin: header1 },
				columnToMergeTo: { header: userSelectedHeader, headerOrigin: header2 },
				columnsSample,
			});
			if (result.status === TEST_RESULT_STATUSES.SUCCESS) {
				const newMergeOjbect = result.payload;
				this.mergeMap = { ...newMergeOjbect, ...this.mergeMap };
				this.updateHeaders(Object.keys(newMergeOjbect)[0]);
			}
		},
		splitMergedHeader(userSelectedHeader) {
			userSelectedHeader.split(' + ').forEach((header) => Vue.delete(this.mergeMap, header));
			this.updateHeaders(userSelectedHeader.split(' + ')[0]);
		},
		getFieldPossibleSelections(headerName) {
			const { MERGE_SELECT_ITEM, SPLIT_SELECT_ITEM } = ColumnMapperService;
			const currentSelection = this.getNewHeaderFromUserSelectedHeader(headerName);
			const selectionOptions = currentSelection.value ? [currentSelection, ...this.remainingColumnOptions] : this.remainingColumnOptions;
			let optionsList = [
				{ divider: true },
				...new Set(selectionOptions),
				{ divider: true },
				headerName.includes(' + ') ? SPLIT_SELECT_ITEM : MERGE_SELECT_ITEM,
			];
			optionsList = selectionOptions.length > 0 ? optionsList : [];
			return [ColumnMapperService.DONT_MAP_SELECT_ITEM, ...optionsList].filter((item) => !isNullOrUndefinedOrEmpty(item));
		},
		getNewHeaderFromUserSelectedHeader(userSelectedHeader) {
			const originHeader = this.getOriginalHeaderFromUserSelectedHeader((userSelectedHeader || '').split(' + ')[0]);
			const newHeader = ColumnMapperService.getNewHeaderFromOriginal(this.columnsMapping, originHeader);
			return { text: newHeader, value: newHeader, id: newHeader };
		},
		setMappingEditorHoveredColumn(userSelectedHeader) {
			const originColumnNames = (userSelectedHeader || '')
				.split(' + ')
				.map((c) => this.getOriginalHeaderFromUserSelectedHeader(c))
				.filter((a) => !isNullOrUndefinedOrEmpty(a));
			return this.$emit('setMappingEditorHoveredColumn', originColumnNames);
		},
		updateHeaders(userSelectedHeader) {
			this.setMergeHeaders(null);
			const headerMap = this.newHeaderRow;
			const originHeadersToSet = Object.keys(headerMap).filter((hr) => userSelectedHeader.includes(headerMap[hr]));
			const newColumnsMap = ColumnMapperService.getNewMappedColumn(this.columnsMapping, 'dont_map', originHeadersToSet);
			this.$emit('updateState', { ...newColumnsMap });
		},
		openColumnsPreviewDialog(userSelectedHeader) {
			const originHeaders = userSelectedHeader.split(' + ').map(this.getOriginalHeaderFromUserSelectedHeader);
			AppDialogManagerService.openColumnsPreviewDialog({
				userSelectedHeader,
				columnsSample: ColumnMapperService.getColumnPreviewSample(this.rows, originHeaders),
			});
		},
	},
	data() {
		return {
			editedHeaders: [],
			userSelectedMergingHeader: null,
			mergeMap: {},
		};
	},
};
</script>

<style scoped lang="scss">
.columns-mapper {
	$bg-color: #fbfbfd;
	$border-color: var(--border-color);
	color: var(--primary-font-color);

	.eye-svg {
		display: none;
	}
}
</style>
